import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuthStore } from '../store/authStore';

interface ColorTheme {
  primary: string;
  secondary: string;
  'secondary-dark': string;
  accent: string;
  text: string;
  background: string;
  sidebar: string;
  border: string;
}

interface CmsInfo {
  name: string;
  slug: string;
  logoUrl: string;
}

interface ColorContextType {
  colors: ColorTheme;
  cmsInfo: CmsInfo;
  updateColors: (newColors: Partial<ColorTheme>) => void;
  updateCmsInfo: (newInfo: Partial<CmsInfo>) => void;
  generateColorVariants: (color: string) => { light: string; dark: string };
  generateThemeFromUserColors: (userColors: any) => ColorTheme;
  loadExternalCmsData: (cmsData: any) => void;
}

const ColorContext = createContext<ColorContextType | undefined>(undefined);

// Função para converter hex para HSL
const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

// Função para converter HSL para hex
const hslToHex = (h: number, s: number, l: number): string => {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Função para gerar variações de cor
const generateColorVariants = (color: string): { light: string; dark: string } => {
  const hsl = hexToHsl(color);
  
  const light = hslToHex(hsl.h, Math.max(0, hsl.s - 20), Math.min(100, hsl.l + 20));
  const dark = hslToHex(hsl.h, Math.min(100, hsl.s + 20), Math.max(0, hsl.l - 20));
  
  return { light, dark };
};

// Função para gerar tema a partir das cores do usuário
const generateThemeFromUserColors = (userColors: any): ColorTheme => {
  if (!userColors || !userColors.primary || !userColors.secondary) {
      return {
    primary: '#3B82F6',
    secondary: '#10B981',
    'secondary-dark': '#059669',
    accent: '#F59E0B',
    text: '#1F2937',
    background: '#F9FAFB',
    sidebar: '#10B981',
    border: '#E5E7EB'
  };
  }

  const primaryVariants = generateColorVariants(userColors.primary);
  const secondaryVariants = generateColorVariants(userColors.secondary);

  return {
    primary: userColors.primary,
    secondary: userColors.secondary,
    'secondary-dark': secondaryVariants.dark,
    accent: primaryVariants.light,
    text: '#1F2937', // Texto sempre escuro para melhor legibilidade
    background: '#F9FAFB', // Background sempre claro
    sidebar: userColors.secondary,
    border: '#E5E7EB' // Borda padrão
  };
};

export const ColorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuthStore();
  const [colors, setColors] = useState<ColorTheme>({
    primary: '#3B82F6',
    secondary: '#10B981',
    'secondary-dark': '#059669',
    accent: '#F59E0B',
    text: '#1F2937',
    background: '#F9FAFB',
    sidebar: '#10B981',
    border: '#E5E7EB'
  });
  const [cmsInfo, setCmsInfo] = useState<CmsInfo>({
    name: 'U9ion CMS',
    slug: 'u9ion',
    logoUrl: ''
  });

  const generateColorVariantsCallback = useCallback(generateColorVariants, []);
  const generateThemeFromUserColorsCallback = useCallback(generateThemeFromUserColors, []);

  // Carregar tema do usuário ao inicializar (apenas para rotas não personalizadas)
  useEffect(() => {
    const loadUserTheme = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        // Verificar se não é uma rota personalizada
        if (window.location.pathname.match(/^\/[^/]+\//)) {
          return; // Deixar o CmsContext gerenciar
        }

        const response = await fetch('/api/users/cms/theme', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.theme) {
            const newTheme = generateThemeFromUserColorsCallback(data.theme.colors);
            setColors(newTheme);

            // Atualizar informações do CMS
            setCmsInfo(prev => ({
              ...prev,
              name: data.theme.cmsName || prev.name,
              slug: data.theme.cmsName?.toLowerCase().replace(/\s+/g, '-') || prev.slug,
              logoUrl: data.theme.logoUrl || prev.logoUrl
            }));
          }
        }
      } catch (error) {
        // Erro ao carregar tema do usuário
      }
    };

    loadUserTheme();
  }, [generateThemeFromUserColorsCallback]);

  // Aplicar cores dinâmicas quando o usuário mudar (apenas para rotas não personalizadas)
  useEffect(() => {
    // Verificar se não é uma rota personalizada
    if (window.location.pathname.match(/^\/[^/]+\//)) {
      return; // Deixar o CmsContext gerenciar
    }

    if (user && user.colors) {
      console.log('🎨 ColorContext - Aplicando cores do usuário:', user.colors);
      const newTheme = generateThemeFromUserColorsCallback(user.colors);
      setColors(newTheme);

      // Atualizar informações do CMS
      if (user.cmsName || user.cmsSlug || user.logoUrl) {
        setCmsInfo(prev => ({
          ...prev,
          name: user.cmsName || prev.name,
          slug: user.cmsSlug || prev.slug,
          logoUrl: user.logoUrl || prev.logoUrl
        }));
      }
    } else if (user && !user.colors) {
      console.log('🎨 ColorContext - Usuário sem cores, usando padrão');
      // Se o usuário não tem cores, usar cores padrão
      const defaultTheme = {
        primary: '#3B82F6',
        secondary: '#10B981',
        'secondary-dark': '#059669',
        accent: '#F59E0B',
        text: '#1F2937',
        background: '#F9FAFB',
        sidebar: '#10B981',
        border: '#E5E7EB'
      };
      setColors(defaultTheme);
    }
  }, [user, generateThemeFromUserColorsCallback]);

  const updateColors = useCallback((newColors: Partial<ColorTheme>) => {
    setColors(prev => ({ ...prev, ...newColors }));
  }, []);

  const updateCmsInfo = useCallback((newInfo: Partial<CmsInfo>) => {
    setCmsInfo(prev => ({ ...prev, ...newInfo }));
  }, []);

  const loadExternalCmsData = useCallback((cmsData: any) => {
    if (cmsData.colors) {
      const newTheme = generateThemeFromUserColorsCallback(cmsData.colors);
      setColors(newTheme);
    }
    
    if (cmsData.cmsName || cmsData.slug || cmsData.logoUrl) {
      setCmsInfo(prev => ({
        ...prev,
        name: cmsData.cmsName || prev.name,
        slug: cmsData.slug || prev.slug,
        logoUrl: cmsData.logoUrl || prev.logoUrl
      }));
    }
  }, [generateThemeFromUserColorsCallback]);

  // Escutar evento de cores do CMS para sincronização
  useEffect(() => {
    const handleCmsColorsUpdate = (event: CustomEvent) => {
      const { colors: cmsColors } = event.detail;
      if (cmsColors) {
        const newTheme = generateThemeFromUserColorsCallback(cmsColors);
        setColors(newTheme);
      }
    };

    window.addEventListener('cmsColorsUpdated', handleCmsColorsUpdate as EventListener);
    
    return () => {
      window.removeEventListener('cmsColorsUpdated', handleCmsColorsUpdate as EventListener);
    };
  }, [generateThemeFromUserColorsCallback]);

  // A integração com CmsContext agora é feita via useCmsIntegration hook

  return (
    <ColorContext.Provider value={{
      colors,
      cmsInfo,
      updateColors,
      updateCmsInfo,
      generateColorVariants: generateColorVariantsCallback,
      generateThemeFromUserColors: generateThemeFromUserColorsCallback,
      loadExternalCmsData
    }}>
      {children}
    </ColorContext.Provider>
  );
};

export const useColors = () => {
  const context = useContext(ColorContext);
  if (context === undefined) {
    throw new Error('useColors must be used within a ColorProvider');
  }
  return context;
};